import { setlist } from '@/utils/typesense';
import * as Sentry from '@sentry/nextjs';
import { HydrationBoundary, QueryClient, type DehydratedState } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import NewAsset from '@/components/Icons/NewAsset';
import { SEO } from '@/components/SEO';
import { extractColor } from '@/lib/extractColor';
import { isAssetNew } from '@/lib/utils';
import { Header, HeaderImage, HeaderMobileHero, HeaderSubtitle, HeaderTitle, HeaderTrackActions, HeaderWaveform } from '@/components/ui/header';
import { Echo } from '@music-vine/cadence/ui';
import { VirtualizedTrackList } from '@/components/VirtualizedTrackList';
import type { AudioTrack } from '@/features/AudioPlayer';
import { createLoadedQueueAtom } from '@/features/AudioPlayer';
import { useSimilarSfxSearch } from '@/hooks/search/use-similar-sfx-search';
import { useListingInfiniteScrollActive } from '@/hooks/useListingInfiniteScrollActive';
import { useSchema } from '@/hooks/seo/useSchema';
import { useSfxResultsWithContributorSlugs } from '@/hooks/useSfxResultsWithContributorSlugs';
import type { SearchResponseHit, SfxDocument, SfxWithEngagementDocument } from '@music-vine/setlist';
import { setCacheTag } from '@/utils/cache-tags';
import { NO_STORE } from '@/utils/pages/cache-headers';
import { isString } from '@/utils/is-string';
import { secondsToISO } from '@/utils/schema/seconds-to-iso';
import { getDehydratedState, prefetchSimilarSfxResults } from '@/utils/search/sfx-similarity-ssr';
import { getSfxPageDescFromTypesense, getSfxPageTitleFromTypesense } from '@/utils/seo/sfx-page-meta';
import { normalizeSfxDocumentForSearch } from '@/utils/track-documents';
import { typesenseSfxToTrack } from '@/utils/typesense/typesense-sfx-to-track';
import type { GetServerSideProps, NextPage } from 'next';
import type { AudioObject } from 'schema-dts';
export type SfxPageParams = {
  sfxSlug: string;
  sfxId: string;
  variantId: string;
  [key: string]: string | string[] | undefined;
};
interface SfxPageProps {
  backgroundColor?: string | null;
  contributorSlug?: string | null;
  dehydratedState?: DehydratedState;
  typesenseSfx?: SfxWithEngagementDocument | null;
  variantName?: string | null;
  variants?: SfxWithEngagementDocument[];
}

// Component for displaying similar SFX
const SimilarSfxSection = memo(({
  referenceSfx,
  contextPath
}: {
  referenceSfx: SfxWithEngagementDocument;
  contextPath: string;
}) => {
  const {
    data: searchData,
    hasNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    loadMoreRef,
    fetchNextPage
  } = useSimilarSfxSearch({
    referenceSfx,
    excludeSfxId: referenceSfx.sfx_id,
    contextPath,
    autoSetupQueue: true
  });

  // Hide the site footer while more similar SFX remain, reveal it at the
  // true end (group B of the show-more experiment; group A hides the
  // footer on /sfx/* routes wholesale).
  useListingInfiniteScrollActive(!!hasNextPage);

  // Extract and transform search results
  const sfxResults = useMemo(() => {
    if (!searchData?.results) {
      return [];
    }
    return searchData.results;
  }, [searchData]);

  // Convert SearchResponseHit to SearchDocument format
  const convertedResults = useMemo(() => sfxResults.map((hit: SearchResponseHit<SfxDocument>) => normalizeSfxDocumentForSearch(hit.document as SfxWithEngagementDocument)), [sfxResults]);

  // Resolve contributor slugs for every contributor present in the list so
  // each SFX row can render a clickable contributor link. sfx.v3 docs only
  // ship contributor_name; the slug comes from contributors.v1.
  const enrichedResults = useSfxResultsWithContributorSlugs(convertedResults);
  return <>
        <VirtualizedTrackList results={enrichedResults} isLoading={isLoading} isError={isError} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} initialRect={{
      width: 800,
      height: 600
    }} />
        {!!hasNextPage && <div ref={loadMoreRef} className="h-20" />}
      </>;
});
SimilarSfxSection.displayName = 'SimilarSfxSection';

// Helper to convert SfxWithEngagementDocument to AudioTrack
const sfxToAudioTrack = (sfx: SfxWithEngagementDocument): AudioTrack => ({
  id: Number(sfx.sfx_variant_id),
  sfx_id: Number(sfx.sfx_id),
  sfx_variant_id: Number(sfx.sfx_variant_id),
  name: sfx.name,
  slug: sfx.sfx_slug,
  contributor_image: sfx.image,
  contributor_name: sfx.contributor_name,
  version_preview_uri: sfx.version_preview_uri,
  version_length: sfx.version_length,
  is_premium: sfx.is_premium,
  styles: sfx.styles,
  type: 'sfx'
});

// Component for displaying other variants in the set
const VariantsSection = memo(({
  variants,
  sfxName,
  contextPath,
  contributorSlug
}: {
  variants: SfxWithEngagementDocument[];
  sfxName: string;
  contextPath: string;
  contributorSlug: string | null;
}) => {
  const createLoadedQueue = useSetAtom(createLoadedQueueAtom);

  // Convert variants to AudioTracks for the queue
  const variantAudioTracks = useMemo<AudioTrack[]>(() => variants.map(sfxToAudioTrack), [variants]);

  // Create a loaded queue for variants with unique source
  useEffect(() => {
    if (variantAudioTracks.length === 0) {
      return;
    }
    createLoadedQueue({
      tracks: variantAudioTracks,
      context: {
        source: `${contextPath}/variants`,
        sourceType: 'browse',
        sourceName: 'Other SFX in this set',
        sourceId: 'variants'
      },
      replace: true
    });
  }, [variantAudioTracks, contextPath, createLoadedQueue]);

  // Convert variants to SearchDocument format for VirtualizedTrackList.
  // Splice in the SSR-resolved contributor slug so each row's contributor
  // name becomes a clickable link — variants always share one contributor.
  const convertedVariants = useMemo(() => variants.map(variant => {
    const normalized = normalizeSfxDocumentForSearch(variant);
    const variantSuffix = variant.name === sfxName ? '' : ` (${variant.name.replace(sfxName, '').trim()})`;
    return {
      ...normalized,
      name: `${sfxName}${variantSuffix}`.trim(),
      contributor_slug: contributorSlug ?? undefined
    };
  }), [variants, sfxName, contributorSlug]);
  if (variants.length === 0) {
    return null;
  }
  return <div className="mx-auto mb-12 w-full space-y-4 px-container">
        <Echo asChild>
          <h2>Other SFX in this set</h2>
        </Echo>
        <VirtualizedTrackList results={convertedVariants} isLoading={false} isError={false} hasNextPage={false} isFetchingNextPage={false} fetchNextPage={() => Promise.resolve()} pageQueueAtom={null} initialRect={{
      width: 800,
      height: 600
    }} />
      </div>;
});
VariantsSection.displayName = 'VariantsSection';
const SfxPage: NextPage<SfxPageProps> = memo(({
  backgroundColor,
  dehydratedState,
  typesenseSfx,
  variants = [],
  variantName,
  contributorSlug
}) => {
  const router = useRouter();
  const {
    sfxSlug,
    sfxId,
    variantId
  } = router.query;
  const contextPath = `/sfx/${sfxSlug}/${sfxId}/${variantId}`;

  // Convert Typesense document to Track type for header components
  const track = useMemo(() => typesenseSfx ? typesenseSfxToTrack(typesenseSfx) : null, [typesenseSfx]);

  // Convert Typesense document to AudioTrack for the queue system
  const audioTrack = useMemo<AudioTrack | null>(() => {
    if (!typesenseSfx) {
      return null;
    }
    return sfxToAudioTrack(typesenseSfx);
  }, [typesenseSfx]);

  // Set up queue for the header track only
  const createLoadedQueue = useSetAtom(createLoadedQueueAtom);
  useEffect(() => {
    if (!audioTrack) {
      return;
    }

    // Create a loaded queue with just the header SFX
    // Variants and similar SFX have their own queues
    createLoadedQueue({
      tracks: [audioTrack],
      context: {
        source: `${contextPath}/header`,
        sourceType: 'browse',
        sourceName: audioTrack.name,
        sourceId: String(audioTrack.id)
      },
      replace: true
    });
  }, [audioTrack, contextPath, createLoadedQueue]);

  // SEO metadata using Typesense data
  const pageMetaTitle = typesenseSfx ? getSfxPageTitleFromTypesense(typesenseSfx, variantName ?? undefined) : 'SFX - Uppbeat';
  const pageMetaDesc = typesenseSfx ? getSfxPageDescFromTypesense(typesenseSfx) : '';

  // Canonical URL
  const canonicalLink = typesenseSfx ? new URL(`sfx/${typesenseSfx.sfx_slug}/${typesenseSfx.sfx_id}/${typesenseSfx.sfx_variant_id}`, process.env.NEXT_PUBLIC_FE_BASE_URL ?? undefined).href : null;

  // Format display title with variant suffix
  const displayTitle = useMemo(() => {
    if (!typesenseSfx) {
      return 'SFX Not Found';
    }
    if (variantName && variantName.trim() !== '') {
      return `${typesenseSfx.name} (${variantName})`;
    }
    return typesenseSfx.name;
  }, [typesenseSfx, variantName]);
  const isNew = useMemo(() => isAssetNew(typesenseSfx?.set_live ?? 0), [typesenseSfx?.set_live]);

  // JSON-LD schema for AudioObject
  const sfxSchema = useSchema<AudioObject>({
    '@type': 'AudioObject',
    name: displayTitle,
    description: pageMetaDesc,
    duration: secondsToISO(typesenseSfx?.version_length),
    contentUrl: typesenseSfx?.version_preview_uri,
    encodingFormat: 'audio/mpeg',
    creator: {
      '@type': 'Person',
      name: typesenseSfx?.contributor_name
    },
    thumbnailUrl: typesenseSfx?.image,
    keywords: typesenseSfx?.keywords?.join(', ')
  });
  if (!(typesenseSfx && track)) {
    return <section className="page-container">
          <SEO title="SFX Not Found - Uppbeat" description="The requested sound effect could not be found.">
            <meta name="robots" content="noindex, nofollow" />
          </SEO>
          <Header ticks={false}>
            <HeaderTitle>SFX Not Found</HeaderTitle>
            <HeaderSubtitle>
              The requested sound effect could not be found.
            </HeaderSubtitle>
          </Header>
        </section>;
  }
  return <HydrationBoundary state={dehydratedState}>
        <section className="sfx-container page-container">
          <SEO title={pageMetaTitle} description={pageMetaDesc}>
            {!!canonicalLink && <link key="canonical" rel="canonical" href={canonicalLink} />}
            {!!sfxSchema && <script type="application/ld+json" {...jsonLdScriptProps<AudioObject>(sfxSchema)} key={`sfx-jsonld-${typesenseSfx.sfx_variant_id}`} />}
          </SEO>
          <Header ticks={false}>
            <HeaderMobileHero alt={typesenseSfx.contributor_name} backgroundColor={backgroundColor} showPremiumStar={!!audioTrack?.is_premium} src={typesenseSfx.image} track={track} />
            <HeaderImage src={typesenseSfx.image} alt={typesenseSfx.contributor_name} className="rounded-full" showPremiumStar={!!audioTrack?.is_premium} containerClassName="hidden md:block" />

            <HeaderTitle badge={isNew ? <NewAsset /> : undefined}>
              {displayTitle}
            </HeaderTitle>
            <HeaderSubtitle className="-mt-1 md:mt-0">
              <div className="flex flex-wrap gap-x-3 gap-y-2">
                {contributorSlug ? <Link href={`/contributors/${contributorSlug}?type=sfx`} className="font-semibold text-gray-700 hover:underline dark:text-gray-200">
                    {typesenseSfx.contributor_name}
                  </Link> : <span className="font-semibold text-gray-700 dark:text-gray-200">
                    {typesenseSfx.contributor_name}
                  </span>}
              </div>
            </HeaderSubtitle>
            <HeaderTrackActions className="mt-4 md:mt-0" track={track} />
            <HeaderWaveform track={track} />
          </Header>

          {/* Other SFX in this set (variants) */}
          {variants.length > 0 && <VariantsSection variants={variants} sfxName={typesenseSfx.name} contextPath={contextPath} contributorSlug={contributorSlug ?? null} />}

          {/* Similar SFX */}
          <div className="fl-mt-8/12 mx-auto mb-12 w-full space-y-4 px-container first-of-type:mt-0">
            <Echo asChild>
              <h2>You may also like</h2>
            </Echo>
            <SimilarSfxSection referenceSfx={typesenseSfx} contextPath={contextPath} />
          </div>
        </section>
      </HydrationBoundary>;
});
SfxPage.displayName = 'SfxPage';
export const getServerSideProps: GetServerSideProps<SfxPageProps, SfxPageParams> = async context => {
  const {
    res,
    params,
    resolvedUrl
  } = context;
  const {
    sfxSlug,
    sfxId,
    variantId
  } = params ?? {};
  if (!(isString(sfxSlug) && isString(sfxId) && isString(variantId))) {
    return {
      notFound: true
    };
  }
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY
      }
    }
  });
  try {
    // Fetch SFX from Typesense
    const typesenseSfx = await setlist.getSfx(sfxId, variantId);
    if (!typesenseSfx) {
      res.setHeader('Cache-Control', NO_STORE);
      return {
        notFound: true
      };
    }

    // Check if slug matches, redirect if not
    if (sfxSlug !== typesenseSfx.sfx_slug) {
      return {
        redirect: {
          destination: `/sfx/${typesenseSfx.sfx_slug}/${sfxId}/${variantId}`,
          permanent: true
        }
      };
    }

    // Cache the page for 3 days. Set on the success path only, so 404s,
    // redirects and errors aren't cached (or tagged) for three days.
    res.setHeader('Cache-Control', 'public, s-maxage=259200, stale-while-revalidate');
    setCacheTag(res, resolvedUrl);

    // Fetch variants + prefetch similar SFX + extract header colour + resolve
    // contributor slug in parallel. The contributor slug lookup goes through
    // contributors.v1 because sfx.v3 documents don't carry contributor_slug —
    // they only have contributor_name. Array order matches the destructuring:
    // [variants, _prefetched, backgroundColor, contributorSlug].
    const [variants,, backgroundColor, contributorSlug] = await Promise.all([setlist.getSfxVariants(sfxId, variantId), prefetchSimilarSfxResults({
      queryClient,
      referenceSfx: typesenseSfx,
      ssrContext: context
    }), typesenseSfx.image ? extractColor(typesenseSfx.image) : Promise.resolve(undefined), setlist.getContributorSlugByName(typesenseSfx.contributor_name)]);

    // Extract variant name for the current variant (if it differs from base name)
    // The variant name is typically stored in the name field as a suffix
    // e.g., base name "Whoosh" with variants "Whoosh Short", "Whoosh Long"
    let variantName: string | undefined;

    // Find if there's a naming pattern across variants
    if (variants.length > 0) {
      // If there are other variants, they likely have different suffixes
      // We can try to extract the variant-specific part of the name
      const allNames = [typesenseSfx, ...variants].map(v => v.name);
      const commonPrefix = findCommonPrefix(allNames);
      if (commonPrefix && commonPrefix.length < typesenseSfx.name.length) {
        variantName = typesenseSfx.name.replace(commonPrefix, '').trim();
      }
    }
    return {
      props: {
        backgroundColor: backgroundColor ?? null,
        dehydratedState: getDehydratedState(queryClient),
        typesenseSfx,
        variants,
        variantName: variantName ?? null,
        contributorSlug: contributorSlug ?? null
      }
    };
  } catch (error) {
    // Report error to Sentry with context
    Sentry.withScope(scope => {
      scope.setExtra('sfxSlug', sfxSlug);
      scope.setExtra('sfxId', sfxId);
      scope.setExtra('variantId', variantId);
      scope.setTag('page', 'sfx-individual');
      Sentry.captureException(error);
    });
    res.setHeader('Cache-Control', NO_STORE);
    return {
      notFound: true
    };
  }
};

/**
 * Find the common prefix among an array of strings
 */
function findCommonPrefix(strings: string[]): string {
  if (strings.length === 0) {
    return '';
  }
  if (strings.length === 1) {
    return strings[0] ?? '';
  }
  let prefix = strings[0] ?? '';
  for (let i = 1; i < strings.length; i++) {
    const str = strings[i];
    if (!str) {
      continue;
    }
    while (prefix.length > 0 && !str.startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
    }
  }
  return prefix;
}
export default SfxPage;